import React, { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { useAuth } from '../../hooks/use-auth';
import * as usersService from '../../services/api/users';
import StyledInput from '../common/form/styled-input';
import { strings, parameters } from '../../localization/strings';
import StyledLabel from '../common/form/styled-label';
import StyledError from '../common/form/styled-error';
import CommonAnimatedLoader from '../common/animated/loader';
import CommonButtonDefault from '../common/buttons/default';
import { ModalKeys } from '../../context/modals';
import { useModal } from '../../hooks/use-modal';
import { useSite } from '../../hooks/use-site';
import AlertPrimary from '../common/alerts/alert-primary';
import { getLocalizedPath } from '../../helpers/localization-helper';
import { pushGTMEvent, GTM_EVENTS } from '../../utils/gtm-events';
import { ShopifyOrderDetails } from '../../services/api/orders';
import { setAnswerCookie } from '../../context/questionnaire';

interface ComponentProps {
  orderHash: string;
  shopifyOrderId: string;
  orderDetails: ShopifyOrderDetails;
  onLoadingChange?: (loading: boolean) => void;
}

type Inputs = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  agreeMarketing: boolean;
  agreeResearch: boolean;
  agreeTerms: boolean;
  agreeRemoteConsultation: boolean;
  language: string;
};

function ShopifyRegisterForm({ orderHash, shopifyOrderId, orderDetails, onLoadingChange }: ComponentProps) {
  const { setUserToken } = useAuth();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const updateLoading = (loading: boolean) => {
    setIsLoading(loading);
    onLoadingChange?.(loading);
  };
  const { setVisible } = useModal();
  const [allChecked, setAllChecked] = useState(false);

  const { locale, region } = useSite();
  const [errorMessage] = useState<string>('');

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    setValue,
  } = useForm<Inputs>({
    defaultValues: {
      firstName: orderDetails.firstName,
      lastName: orderDetails.lastName,
      email: orderDetails.email,
      phone: orderDetails.phone || '',
      language: locale,
    },
  });

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    try {
      updateLoading(true);
      const token = await usersService.register({
        ...data,
        email: data.email.trim(),
        orderHash,
        region: region || orderDetails.region,
      });

      if (orderDetails.answerHash) {
        setAnswerCookie(orderDetails.answerHash);
      }

      await setUserToken(token, false);

      pushGTMEvent(GTM_EVENTS.COMPLETE_REGISTRATION);

      const dashboardPath = parameters().dashboard;
      const targetPath = getLocalizedPath(dashboardPath, locale, region);

      window.location.href = targetPath;
    } catch (e) {
      updateLoading(false);
      if (e instanceof Error) {
        setError('email', { message: e.message });
      }
    }
  };

  const onOpenModal = (key: string) => {
    setVisible(key, true);
  };

  const updateCheckbox = () => {
    const newValue = !allChecked;

    setAllChecked(newValue);
    setValue('agreeTerms', newValue);
    setValue('agreeResearch', newValue);
    setValue('agreeMarketing', newValue);
  };

  return isLoading ? (
    <CommonAnimatedLoader />
  ) : (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        {errorMessage && (
          <div className="container max-w-4xl mx-auto mt-3">
            <AlertPrimary type="danger" title={strings().error.common.title} />
          </div>
        )}

        <div>
          <StyledLabel htmlFor="name">
            {strings().form.name}
            <span className="text-primary">*</span>
          </StyledLabel>
          <StyledInput
            {...register('firstName', { required: true })}
            fullWidth
            id="firstName"
            name="firstName"
            autoComplete="given-name"
            className={` ${errors.firstName && `!border-red-500`} `}
            type="text"
          />
          {errors.firstName && <StyledError>{strings().error.fieldIsRequired}</StyledError>}
        </div>

        <div className="mt-5">
          <StyledLabel>
            {strings().form.surname}
            <span className="text-primary">*</span>
          </StyledLabel>
          <StyledInput
            fullWidth
            className={` ${errors.lastName && `!border-red-500`} `}
            type="text"
            autoComplete="family-name"
            {...register('lastName', { required: true })}
          />
          {errors.lastName && <StyledError>{strings().error.fieldIsRequired}</StyledError>}
        </div>

        <div className="mt-5">
          <StyledLabel>
            {strings().form.email}
            <span className="text-primary">*</span>
          </StyledLabel>
          <StyledInput
            fullWidth
            className={` ${errors.email && `!border-red-500`} bg-gray-100`}
            type="email"
            autoComplete="email"
            disabled
            {...register('email', { required: true })}
          />
          {errors.email && (
            <StyledError>
              {errors.email.message ? strings().error.email.isUsed : strings().error.email.required}
            </StyledError>
          )}
        </div>

        <div className="mt-5">
          <StyledLabel>
            {strings().form.password}
            <span className="text-primary">*</span>
          </StyledLabel>
          <StyledInput
            fullWidth
            className={` ${errors.password && `!border-red-500`} `}
            type="password"
            autoFocus
            {...register('password', { required: true, minLength: parameters().password.minLength })}
          />
          {errors.password && (
            <StyledError>
              {errors.password.type === 'required' && strings().error.fieldIsRequired}
              {errors.password.type === 'minLength' &&
                strings().error.password.minLength.replace('{length}', `${parameters().password.minLength}`)}
            </StyledError>
          )}
          <div className="text-gray-400 text-sm mt-2">{strings().form.atLeastEightSymbols}</div>
        </div>

        <div className="mt-5">
          <div className="flex items-center">
            <input
              className="h-4 w-4 text-blue-900 bg-white border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-0 focus:border-gray-300 checked:border-transparent"
              id="agreeAll"
              type="checkbox"
              onClick={() => updateCheckbox()}
            />
            <label htmlFor="agreeAll" className="ml-2 block text-sm align-middle leading-6 text-gray-900">
              <strong>{strings().checkout.checkBoxAgreeAll}</strong>
            </label>
          </div>
        </div>

        <div className="mt-5">
          <div className="flex items-center">
            <input
              className="h-4 w-4 text-blue-900 bg-white border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-0 focus:border-gray-300 checked:border-transparent"
              id="agreeTerms"
              type="checkbox"
              {...register('agreeTerms', { required: true })}
            />
            <label htmlFor="agreeTerms" className="ml-2 block text-sm align-middle leading-6 text-gray-900">
              {strings().checkout.checkboxIHaveReadAnd}{' '}
              <CommonButtonDefault className="!text-sm" link onClick={() => onOpenModal(ModalKeys.Privacy)}>
                {strings().checkout.checkboxPrivacyPolicy}
              </CommonButtonDefault>{' '}
              {strings().checkout.checkboxAnd}{' '}
              <CommonButtonDefault className="!text-sm" link onClick={() => onOpenModal(ModalKeys.Terms)}>
                {strings().checkout.checkBoxTermsOfService}
              </CommonButtonDefault>
            </label>
          </div>
        </div>
        {errors.agreeTerms && <StyledError>{strings().error.agreeTerms.required}</StyledError>}

        <div className="flex items-center mt-3">
          <input
            className="h-4 w-4 text-blue-900 bg-white border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-0 focus:border-gray-300 checked:border-transparent"
            id="agreeResearch"
            type="checkbox"
            {...register('agreeResearch', { required: true })}
          />
          <label htmlFor="agreeResearch" className="ml-2 block text-sm align-middle leading-6 text-gray-900">
            {strings().checkout.checkboxIAgreeThatImunodiagnostika}
          </label>
        </div>
        {errors.agreeResearch && <StyledError>{strings().error.required}</StyledError>}

        <div className="flex items-center mt-3">
          <div className="flex items-center h-5">
            <input
              className="h-4 w-4 text-blue-900 bg-white border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-0 focus:border-gray-300 checked:border-transparent"
              id="agreeMarketing"
              type="checkbox"
              {...register('agreeMarketing', {})}
            />
          </div>
          <label htmlFor="agreeMarketing" className="ml-2 text-sm text-gray-900">
            {strings().checkout.checkboxIAgreeToGetDoctorAdvice}
          </label>
        </div>

        <div className="mt-5 md:mt-7 flex justify-end">
          <CommonButtonDefault primary type="submit">
            {strings().button.createAccount}
          </CommonButtonDefault>
        </div>
        <div
          className="flex justify-end text-end mt-3"
          dangerouslySetInnerHTML={{ __html: strings().checkout.dataIsSecured }}
        />
      </form>
    </div>
  );
}

ShopifyRegisterForm.defaultProps = {
  onLoadingChange: undefined,
};

export default ShopifyRegisterForm;
